import React, { useState } from 'react';
import { ChevronDown, Trash2 } from 'lucide-react';
import { rb, RBField, RBInput, RBTextArea, RBChipInput, RBAddButton } from '../theme';
import { LANGUAGE_SUGGESTIONS } from '../../profile/constants';
import AIEnhanceButton from '../AIEnhanceButton';
import { emptyProject, emptyCertification } from '../sectionShapes';

function Group({ title, count, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-2xl overflow-hidden ${rb.glassSoft}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3.5 text-slate-100">
        <span className="font-semibold text-sm">
          {title}
          {count > 0 && <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${rb.chip}`}>{count}</span>}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-white/10 space-y-3">{children}</div>}
    </div>
  );
}

export default function ExtrasStep({ state, setState, sessionId }) {
  const updateList = (listKey, i, key, value) =>
    setState((s) => ({
      ...s,
      [listKey]: s[listKey].map((row, idx) => (idx === i ? { ...row, [key]: value } : row)),
    }));
  const removeFromList = (listKey, i) =>
    setState((s) => ({ ...s, [listKey]: s[listKey].filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">
        All of these are optional — add what makes you stand out, skip the rest.
      </p>

      <Group title="Projects" count={state.projects.length}>
        {state.projects.map((p, i) => (
          <div key={i} className="space-y-2 pt-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-violet-300">Project {i + 1}</span>
              <button type="button" onClick={() => removeFromList('projects', i)} className="p-1 text-red-400" aria-label="Remove project">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <RBInput value={p.name} onChange={(e) => updateList('projects', i, 'name', e.target.value)} placeholder="Project name" />
            <RBTextArea rows={2} value={p.description} onChange={(e) => updateList('projects', i, 'description', e.target.value)} placeholder="What did you build or do?" />
            <AIEnhanceButton sessionId={sessionId} sectionType="projects" text={p.description} onAccept={(t) => updateList('projects', i, 'description', t)} />
            <RBInput value={p.link} onChange={(e) => updateList('projects', i, 'link', e.target.value)} placeholder="Link (optional)" />
          </div>
        ))}
        <RBAddButton label="Add a project" onClick={() => setState((s) => ({ ...s, projects: [...s.projects, emptyProject()] }))} />
      </Group>

      <Group title="Certifications" count={state.certifications.length}>
        {state.certifications.map((cert, i) => (
          <div key={i} className="space-y-2 pt-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-violet-300">Certificate {i + 1}</span>
              <button type="button" onClick={() => removeFromList('certifications', i)} className="p-1 text-red-400" aria-label="Remove certificate">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <RBInput value={cert.name} onChange={(e) => updateList('certifications', i, 'name', e.target.value)} placeholder="e.g. NSDC Retail Sales Certificate" />
            <div className="grid grid-cols-2 gap-2">
              <RBInput value={cert.issuer} onChange={(e) => updateList('certifications', i, 'issuer', e.target.value)} placeholder="Issued by" />
              <RBInput value={cert.year} onChange={(e) => updateList('certifications', i, 'year', e.target.value)} placeholder="Year" />
            </div>
          </div>
        ))}
        <RBAddButton label="Add a certificate" onClick={() => setState((s) => ({ ...s, certifications: [...s.certifications, emptyCertification()] }))} />
      </Group>

      <Group title="Languages" count={state.languages.length}>
        <RBField label="Languages you speak">
          <RBChipInput
            values={state.languages}
            onChange={(languages) => setState((s) => ({ ...s, languages }))}
            suggestions={LANGUAGE_SUGGESTIONS}
            placeholder="e.g. Hindi, English…"
          />
        </RBField>
      </Group>

      <Group title="Achievements" count={state.achievements.length}>
        {state.achievements.map((a, i) => (
          <div key={i} className="flex items-center gap-2 pt-1">
            <RBInput
              value={a}
              onChange={(e) =>
                setState((s) => ({ ...s, achievements: s.achievements.map((x, idx) => (idx === i ? e.target.value : x)) }))
              }
              placeholder="e.g. Best employee of the month, 3 times"
            />
            <button
              type="button"
              onClick={() => setState((s) => ({ ...s, achievements: s.achievements.filter((_, idx) => idx !== i) }))}
              className="p-1.5 text-red-400 shrink-0"
              aria-label="Remove achievement"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <RBAddButton label="Add an achievement" onClick={() => setState((s) => ({ ...s, achievements: [...s.achievements, ''] }))} />
      </Group>
    </div>
  );
}
